import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity } from 'react-native';
import { Colors, SIZES } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useAppStore } from '../../store/useAppStore';
import { firestoreService } from '../../services/firestoreService';
import AvisoVistaPrevia from '../../components/AvisoVistaPrevia';

export default function RentChatsScreen() {
  const router = useRouter();
  const ownerUid = useAppStore((state) => state.userFirebaseUid);
  const setActiveChatPartner = useAppStore((state) => state.setActiveChatPartner);

  // AUDIT FIX RENT — Antes `const chats: any[] = []` hardcoded → siempre
  // vacío aunque hubiera negociaciones en Firestore. Ahora escuchamos
  // /rent_negotiations con documentId startsWith ownerUid_ para listar
  // solo los chats de ESTE owner.
  const [chats, setChats] = useState<any[]>([]);
  useEffect(() => {
    if (!ownerUid) return;
    const unsub = firestoreService.listenToMisNegociaciones(ownerUid, (items) => {
      // Map cada doc a la forma que espera la row (name, owner, lastMsg, time).
      // Como los mensajes viven en la sub-colección, aquí solo pintamos el
      // partner. El last msg lo hidratamos best-effort desde el doc.
      // FIX AUDIT — El doc padre ahora trae partnerName/partnerVehicle/renterId
      // directamente (antes se parseaba el chatId con slugs, ilegible). El
      // chatId nuevo es `${ownerId}__${renterId}`; guardamos renterId como
      // partnerUid para reabrir el hilo correcto.
      const mapped = items.map((it: any) => {
        // ⚠️ DE QUÉ LADO ESTOY. Antes esta pantalla daba por hecho que quien la
        // miraba era el DUEÑO (la variable se llama `ownerUid` por eso). Ahora
        // la ven los dos, así que el «otro» es el que no soy yo.
        const soyElDueno = it.ownerId === ownerUid;
        const otroUid = soyElDueno ? it.renterId : it.ownerId;
        return {
        id: it.id,
        name: soyElDueno
          ? it.partnerName || 'Candidato'
          : it.partnerVehicle || 'Dueño del vehículo',
        owner: it.partnerVehicle || 'Vehículo de interés',
        _soyElDueno: soyElDueno,
        _otroUid: otroUid || '',
        lastMsg: it.lastMessage || 'Toca para abrir la conversación',
        time: it.updatedAt?.toDate?.()?.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' }) ?? '',
        unread: it.unreadCount ?? 0,
        _renterId: it.renterId || String(it.id ?? '').split('__')[1] || '',
        _partnerName: it.partnerName || 'Candidato',
        _vehicle: it.partnerVehicle || '',
        };
      });
      setChats(mapped);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [ownerUid]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <Text style={styles.title}>Mensajes</Text>
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        <AvisoVistaPrevia que="los chats de alquiler" />
        {chats.length === 0 && (
          <View style={{ alignItems: 'center', paddingVertical: 40 }}>
            <Ionicons name="chatbubbles-outline" size={48} color={Colors.textSecondary} />
            <Text style={{ color: Colors.textSecondary, marginTop: 12, textAlign: 'center' }}>
              No tienes negociaciones abiertas.{'\n'}Cuando un candidato te contacte, aparecerá aquí.
            </Text>
          </View>
        )}
        {chats.map((chat) => (
          <TouchableOpacity
            key={chat.id}
            style={styles.chatRow}
            onPress={() => {
              // Restaurar activeChatPartner para que rent-car-chat abra el
              // hilo correcto (mismo chatId determinístico).
              // ⚠️ ESTO SOLO SABÍA ABRIR EL HILO DEL DUEÑO.
              // Ponía siempre `partnerUid: chat._renterId` y `role:
              // 'CANDIDATE'` — correcto cuando quien mira es el dueño, y
              // justo al revés cuando es el chofer: `rent-car-chat` arma el
              // chatId con `ownerId` si no eres el dueño, y ese campo no se
              // mandaba nunca. El chofer habría abierto un hilo sin id.
              setActiveChatPartner({
                name: chat._partnerName || chat.name,
                vehicle: chat._vehicle || chat.owner,
                role: chat._soyElDueno ? 'CANDIDATE' : 'RENT_CAR_OWNER',
                partnerUid: chat._otroUid,
                ...(chat._soyElDueno ? {} : { ownerId: chat._otroUid }),
              } as any);
              router.push('/rent-car-chat');
            }}
          >
            <View style={styles.avatar}>
              <Ionicons name="car" size={24} color={Colors.white} />
            </View>
            <View style={styles.chatInfo}>
              <View style={styles.chatHeader}>
                <Text style={styles.chatName}>{chat.name}</Text>
                <Text style={styles.chatTime}>{chat.time}</Text>
              </View>
              <Text style={styles.chatOwner}>{chat.owner}</Text>
              <View style={styles.chatFooter}>
                <Text style={styles.lastMsg} numberOfLines={1}>{chat.lastMsg}</Text>
                {chat.unread > 0 && (
                  <View style={styles.unreadBadge}>
                    <Text style={styles.unreadText}>{chat.unread}</Text>
                  </View>
                )}
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  header: {
    paddingHorizontal: SIZES.padding,
    paddingTop: 60,
    paddingBottom: 15,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Colors.text,
  },
  container: {
    padding: SIZES.padding,
  },
  chatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: Colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  chatInfo: {
    flex: 1,
  },
  chatHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  chatName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
  },
  chatTime: {
    fontSize: 12,
    color: Colors.textSecondary,
  },
  chatOwner: {
    fontSize: 12,
    color: Colors.accent,
    marginBottom: 4,
  },
  chatFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lastMsg: {
    flex: 1,
    fontSize: 14,
    color: Colors.textSecondary,
    marginRight: 10,
  },
  unreadBadge: {
    backgroundColor: Colors.primary,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unreadText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: 'bold',
  }
});
